import Vue from "vue";
import Vuex from "vuex";
import lodash from 'lodash';
import deepdash from 'deepdash';
const _ = deepdash(lodash);

Vue.use(Vuex);

import commons from "../commons/commons";
import { authorizationStore } from "./authorizationStore";
import { commonStore } from "./commonStore";
import moduleGuard from "../commons/moduleGuard";

import VueCookies from 'vue-cookies'
import LocalStorageService from "./LocalStorageService";
import webAppConfig from "../commons/webApp.config";
Vue.use(VueCookies)

const getInitialState = () => ({
    ...commonStore.state,
    modules: {},
    modulePromises: {},
    users: [],
    states: [],
    selection: { type: '', item: null },
    baseServer: null,
    vector: null,
    originalVector: null,
    workspace: null,
    originalWorkspace: null,
    vectorId: null,
    list: null,
    filterText: '',
    filterKeyword: null,
    suggestions: [],
    canvas: {
        information: [],
        states: [],
        flows: [],
        activities: [],
        menus: [],
        actions: [],
        documents: [],
        designer: {},
    },
    isSaving: false,
    autoPush: true,
});

export const store = new Vuex.Store({
    state: getInitialState(),
    mutations: {
        resetState(state) {
            Object.assign(state, getInitialState());
        },
        setClientHeadersWsId(state, data) {
            state.client.defaults.headers['wsId'] = data;
        },
        setIsSaving(state, data) {
            state.isSaving = data;
        },
        updateModule(state, data) {
            state.vector = data;
        },
        setCanvas(state, data) {
            state.canvas = data;
        },
        setVector(state, data) {
            state.vectorId = data;
        },
        setWorkspaceModule(state, data) {
            if (state.workspace) {
                try {
                    let workspaceModule = state.workspace.vectors.find(x => x.id == data.id);
                    if (workspaceModule) {
                        workspaceModule.name = data.name;
                        workspaceModule.emoji = data.emoji;
                    }
                } catch(err) { console.error(err); }

                try {
                    let workspaceMenu = _.findDeep(state.workspace.menus, x => (x.id == data.id), { childrenPath: 'children' });
                    if (workspaceMenu && workspaceMenu.value) {
                        workspaceMenu.value.name = data.name;
                        workspaceMenu.value.emoji = data.emoji;
                        workspaceMenu.value.isProfile = data.isUserCollection;
                        workspaceMenu.value.published = data.published;
                    }
                } catch(err) { console.error(err); }
            }
        },
        commitVector(state, data) {
            if (data == null || state.vectorId != data.id) {
                if (state.workspace && state.workspace.id) {
                    const preference = LocalStorageService.getStudioPreference(webAppConfig.baseServer, state.workspace.id);

                    if (preference && preference.hasOwnProperty("autoPush")) {
                        state.autoPush = !!preference.autoPush;
                    } else {
                        state.autoPush = true;
                    }
                } else {
                    state.autoPush = true;
                }
            }

            state.vector = data;

            if (state.vector && state.vector.ownerWorkspaceId) {
                state.client.defaults.headers['wsId'] = state.vector.ownerWorkspaceId;
                $cookies.set('wsId', state.vector.ownerWorkspaceId);
                LocalStorageService.setWorkspaceId(webAppConfig.baseServer, state.vector.ownerWorkspaceId);
            }

            if (state.vector && !state.vector.ownerWorkspaceId && state.workspace) {
                state.vector.ownerWorkspaceId = state.workspace.id
            }
            if (data) {
                state.vectorId = data.id;
                state.canvas = data.extension;
                state.originalVector = moduleGuard.compactModule(_.cloneDeep(data));
                if (!state.canvas.triggers) {
                    Vue.set(state.canvas, 'triggers', []);
                }
            } else {
                state.vectorId = null;
                state.canvas = null;
                state.originalVector = null;
            }
        },
        commitVectorCanvas(state, data) {
            state.vector.extension = data;
            state.canvas = data;
        },
        commitAutoPush(state, data) {
            state.autoPush = data;
        },
        updateOriginal(state, data) {
            state.originalVector = moduleGuard.compactModule(_.cloneDeep(data));
        },
        commitWorkspace(state, data) {
            state.workspace = data;
            if (data != null) {
                state.client.defaults.headers['workspace'] = data.name;
                state.client.defaults.headers['wsId'] = data.id;
                state.originalWorkspace = _.cloneDeep({ ...data, menus: commons.trimMenu(state.workspace.menus) });
            } else {
                state.client.defaults.headers['workspace'] = null;
                state.client.defaults.headers['wsId'] = null;
                state.originalWorkspace = null;
            }
            $cookies.set('wsId', data.id);
            LocalStorageService.setWorkspaceId(webAppConfig.baseServer, data.id);
            state.users = data.users;
            state.states = data.states;
            state.filterKeyword = null;
        },
        setWorkspaceName(state, data) {
            state.workspace.name = data;
        },
        commitWorkspaceLastUpdatedOn(state, data) {
            state.workspace.lastUpdatedOn = data;
        },
        commitDiagram(state, data) {
            state.canvas.diagram = data;
        },
        commitUsers(state, data) {
            state.users = data;
        },
        commitList(state, data) {
            state.list = data;
        },
        setBaseServer(state, data) {
            state.baseServer = data;
        },
        removeState(state, targetStateId) {
            let stateToRemove = state.canvas.states
                .find(item => item.id == targetStateId);
            if (stateToRemove) {
                let ind = state.canvas.states.indexOf(stateToRemove);
                state.canvas.states.splice(ind, 1);
            }
        },
        removeFlow(state, { targetFlowId, targetActivityId }) {
            let flow = state.canvas.flows.find(flow => flow.id == targetFlowId);
            if (flow) {
                let ind = state.canvas.flows.indexOf(flow);
                state.canvas.flows.splice(ind, 1);

                let alternativeFlow = state.canvas.flows.find(
                    flow => flow.activityId == targetActivityId
                );
                /* if no alternative, delete the activity */
                if (!alternativeFlow) {
                    let activity = state.canvas.activities.find(
                        activity => activity.id == targetActivityId
                    );
                    if (activity) {
                        let actInd = state.canvas.activities.indexOf(activity);
                        state.canvas.activities.splice(actInd, 1);
                    }
                }
            }
        },
        removeActivity(state, targetActivityId) {
            let flows = state.canvas.flows.filter(flow => flow.activityId == targetActivityId);
            flows.forEach(flow => {
                let flowInd = state.canvas.flows.indexOf(flow);
                state.canvas.flows.splice(flowInd, 1);
            });

            let activity = state.canvas.activities.find(activity =>
                activity.id == targetActivityId);
            if (activity) {
                let ind = state.canvas.activities.indexOf(activity);
                state.canvas.activities.splice(ind, 1);
            }
        },
        removeInformation(state, targetInformationId) {
            let information = state.canvas.information.find(info => info.id == targetInformationId);
            if (information) {
                let ind = state.canvas.information.indexOf(information);
                state.canvas.information.splice(ind, 1);
                /* all activity classification */
                state.canvas.activities.forEach(activity => {
                    if (activity.classification) {
                        let toRemoves = activity.classification.filter(x => x.information.id == targetInformationId);
                        toRemoves.forEach(x => {
                            let cladInd = activity.classification.indexOf(x);
                            activity.classification.splice(cladInd, 1);
                        });
                    }
                });
            }
        },
        commitState(state, locusState) {
            if (!locusState.id) {
                locusState.id = commons.newUUID();
                state.canvas.states.push(locusState);
            } else {
                let originalState = state.canvas.states.find(x => x.id == locusState.id);
                originalState.color = locusState.color;
                originalState.name = locusState.name;
                originalState.description = locusState.description;
            }
        },
        commitActivity(state, { activity, flow }) {
            if (!flow && typeof originalFlow !== 'object')
                flow = {}

            //new activity, new flow
            if (!activity.id && !flow.id) {
                activity.id = commons.newUUID();
                state.canvas.activities.push(activity);

                flow.id = commons.newUUID();
                flow.activityId = activity.id;
                flow.activity = activity;
                state.canvas.flows.push(flow);
            }
            //new activity, update flow
            else if (!activity.id) {
                activity.id = commons.newUUID();
                state.canvas.activities.push(activity);

                let originalFlow = state.canvas.flows.find(x => x.id == flow.id);
                originalFlow.activityId = activity.id;
                originalFlow.activity = activity;
                originalFlow.conditional = flow.conditional;
                originalFlow.conditionalStates = flow.conditionalStates;
            }
            //new flow
            else if (!flow.id) {
                let originalActivity = state.canvas.activities.find(x => x.id == activity.id);
                originalActivity.emoji = activity.emoji;
                originalActivity.color = activity.color;

                //update classification
                originalActivity.classification = activity.classification;
                originalActivity.classification.forEach(x => {
                    // x.information = state.canvas.information.find(y => y.id == x.information.id)
                    Object.assign(x, state.canvas.information.find(y => y.id == x.id));
                });

                flow.id = commons.newUUID();
                flow.activityId = activity.id;
                flow.activity = activity;
                state.canvas.flows.push(flow);
            } else {
                let originalActivity = state.canvas.activities.find(x => x.id == activity.id);
                originalActivity.emoji = activity.emoji;
                originalActivity.color = activity.color;

                //update classification
                originalActivity.classification = activity.classification;
                originalActivity.classification.forEach(x => {
                    // x.information = state.canvas.information.find(y => y.id == x.information.id);
                    Object.assign(x, state.canvas.information.find(y => y.id == x.id));
                });

                let originalFlow = state.canvas.flows.find(x => x.id == flow.id);
                originalFlow.activityId = activity.id;
                originalFlow.activity = activity;
                originalFlow.toStateId = flow.toStateId;
                originalFlow.conditional = flow.conditional;
                originalFlow.conditionalStates = flow.conditionalStates;
            }

            //remove same activity from same state
            let toBeRemoved = state.canvas.flows.filter(x => x.id != flow.id
                && x.fromStateId == flow.fromStateId
                && x.activityId == activity.id);
            toBeRemoved.forEach(x => {
                let ind = state.canvas.flows.indexOf(x);
                state.canvas.flows.splice(ind, 1);
            });
        },
        commitInformation(state, information) {
            information.fieldName = commons.camelize(information.displayName)
            if (!information.id) {
                information.id = commons.newUUID();
                state.canvas.information.push(information);
            } else {
                let originalInformation = state.canvas.information.find(x => x.id == information.id);
                Object.assign(originalInformation, information);
            }
        },
        commitWorkDesign(state, { workDesign, activityId }) {
            let activity = state.canvas.activities.find(x => x.id == activityId);
            if (activity) {
                activity.workDesign = workDesign;
            }
        },
        commitUpdateActivity(state, { id, classification, design }) {
            let standards = ['create', 'edit', 'createEditView', 'view', 'quickView', 'createUpdate', 'delete', 'changeStatus', 'duplicate', 'comment', 'assign', 'print'];
            let activity = null;

            if (standards.indexOf(id) > -1) {
                activity = state.canvas.standardActivities.find(x => x.id == id);
            } else {
                activity = state.canvas.activities.find(x => x.id == id);
            }

            if (activity) {
                activity.design = design;
                activity.classification = classification;
            }
        },
        filterElement(state, keyword) {
            state.filterKeyword = keyword;
        },
        suggestions(state, payload) {
            state.suggestions = payload;
        },
        commitUpdateAuthorization(state, payload) {
            if (!state.canvas.authorizations)
                Vue.set(state.canvas, 'authorizations', [])

            state.canvas.authorizations = payload
        },
        setVectorTrigger(state, data) {
            let triggers = state.vector.extension.triggers;
            if (data.id) {
                const findIndex = triggers.findIndex((x) => x.id == data.id);
                if (findIndex > -1) {
                    Vue.set(triggers, findIndex, data);
                } else {
                    triggers.push({...data});
                }
            } else {
                triggers.push({
                    id: commons.newUUID(),
                    ...data,
                });
            }
        },
        removeVectorTrigger(state, id) {
            let triggers = state.vector.extension.triggers;
            const findIndex = triggers.findIndex(x => x.id == id);
            if (findIndex > -1) {
                triggers.splice(findIndex, 1);
            }
        },
        registerModule(state, module) {
            state.modules[module.id] = module;
        },
        setModulePromise(state, { moduleId, promise }) {
            state.modulePromises[moduleId] = promise;

        }
    },
    getters: {
        isSaving: state => state.isSaving,
        uploadFormData: (state) => (payload) => {
            return {
                vectorId: state.vectorId,
                fieldName: payload.fieldName,
                fieldId: payload.fieldId,
                type: payload.formType
            };
        },
        workspaceId: state => state.workspace && state.workspace.id,
        workspaceName: state => state.workspace && state.workspace.name,
        workspaceBeta: state => Boolean(state.workspace && state.workspace.beta),
        workspaceModules: state => (state.workspace && state.workspace.vectors) || [],
        workspaceStates: state => state.workspace && state.workspace.states || [],
        workspaceUsers: state => state.workspace && state.workspace.users || [],
        isUserCollection: state => state.vector && state.vector.isUserCollection,
        originalWorkspaceMenus: state => state.originalWorkspace && state.originalWorkspace.menus,
        vector: state => state.vector || {},        
        vectorTriggers: state => state.vector && state.vector.extension && state.vector.extension.triggers || [],
        canvasInformation: state => state.canvas && state.canvas.information || [],
        canvasMenus: state => state.canvas && state.canvas.menus || [],
        canvasDocuments: state => state.canvas && state.canvas.documents || [],
        canvasActivities: state => state.canvas && state.canvas.activities || [],
        canvasStandardActivities: state => state.canvas && state.canvas.standardActivities || [],
        canvasStates: state => state.canvas && state.canvas.states || [],
        canvasFlows: state => state.canvas && state.canvas.flows || [],
        canvasActions: state => state.canvas && state.canvas.actions || [],
        canvasLogicServices: state => state.canvas && state.canvas.logicServices || [],
        canvasImport: state => state.canvas && state.canvas.import,
        autoPush: state => state.autoPush === false ? false : true
    },
    actions: {
        ...commonStore.actions,
        resetState({ commit }) {
            commit('resetState');
        },
        setClientHeadersWsId({ commit }, workspaceId) {
            commit('setClientHeadersWsId', workspaceId);
        },
        setIsSaving({ commit }, payload) {
            commit('setIsSaving', payload);
        },
        updateModule({ commit }, payload) {
            commit('updateModule', payload);
        },
        loadCanvas(context, payload) {
            let canvas = payload.canvas
            if (canvas && canvas.information && Array.isArray(canvas.information))
                canvas.information.filter(x => x.type === 14 && !x.fields && Array.isArray(x.columns)).forEach(y => y.fields = y.columns)

            context.commit('setCanvas', canvas);
            context.commit('setBaseServer', payload.baseServer);
            context.commit('setVector', payload.vector);

            if (payload.vectorData != null) {
                context.commit('commitVector', payload.vectorData);
            }
        },
        async downloadSchema({ state }) {
            return await state.client.get(`/api/test/schema/${state.workspace.id}`);
        },
        async retrieveDashboard({ state }) {
            return await state.client.get('/api/workspace/studiodashboard');
        },
        deleteState(context, payload) {
            context.commit('removeState', payload.state.id);
            let flows = context.state.canvas.flows.filter(
                flow =>
                    flow.fromStateId == payload.state.id ||
                    flow.toStateId == payload.state.id ||
                    (flow.conditionalStates &&
                        flow.conditionalStates.some(
                            condition => condition.toStateId == payload.state.id
                        ))
            );
            flows.forEach(flow =>
                context.commit('removeFlow', {
                    targetFlowId: flow.id,
                    targetActivityId: flow.activityId
                }));
        },
        deleteFlow(context, payload) {
            context.commit('removeFlow', {
                targetFlowId: payload.flowId,
                targetActivityId: payload.activityId
            });
        },
        deleteActivity(context, payload) {
            context.commit('removeActivity', payload.activity.id);
        },
        deleteInformation(context, payload) {
            context.commit('removeInformation', payload.information.id);
        },
        confirmState(context, payload) {
            context.commit('commitState', payload.state);
        },
        confirmActivity(context, payload) {
            context.commit('commitActivity', payload);
        },
        updateActivity(context, payload) {
            context.commit('commitUpdateActivity', payload);
        },
        confirmInformation(context, payload) {
            context.commit('commitInformation', payload.information);
        },
        confirmWorkDesign(context, payload) {
            context.commit('commitWorkDesign', {
                workDesign: payload.workDesign,
                activityId: payload.activityId
            })
        },
        saveDiagram(context, payload) {
            context.commit('commitDiagram', payload);
        },
        async loadVectorSelection(context, payload) {
            return await context.state.client.get(
                `${context.state.baseServer || ''}/api/m/`)
        },
        testAutomationBlock(context, payload) {
            return context.state.client.post('/api/canvastest/notification', payload);
        },
        async validateAutomation(context, payload) {
            return context.state.client.post('/api/studio/validateautomation', {
                'automationBlocks': payload,
                'vectorId': context.state.vector.id
            });
        },
        testAutomation(context, payload) {
            return context.state.client.post('/api/canvastest/notification', {
                'automation': payload,
                'vectorId': context.state.vectorId
            });
        },
        testDocument(context, payload) {
            return context.state.client.post('/api/canvastest/document', {
                'document': payload,
                'vectorId': context.state.vector.id
            });
        },
        summary(context, payload) {
            return context.state.client.get(`/api/canvastest/summary/${payload}`);
        },
        previewAutomation(context, payload) {
            return context.state.client.post('/api/canvastest/preview', {
                'automation': payload,
                'vectorId': context.state.vectorId
            });
        },
        plugins(context, payload) {
            return context.state.client.get('/api/studio/plugins');
        },
        plugin(context, payload) {
            return context.state.client.get(`/api/studio/plugin/${payload.project}/${payload.id}`);
        },
        loadPackages(context, payload) {
            return context.state.client.post('/api/p/list', {
                "pageSize": payload.pageSize || 6,
                "category": payload.category,
                "searchText": payload.searchText,
            });
        },
        loadPackageDetail(context, payload) {
            return context.state.client.get(`/api/p/${payload}`);
        },
        loadTopics(context) {
            return context.state.client.get('/api/p/topics');
        },
        intention(context, payload) {
            payload.moduleId = context.state.vectorId.toString();
            return context.state.client.post('/api/ai/intention', payload);
        },
        processSchema(context, payload) {
            return context.state.client.post('/api/ai/schema', { schema: payload });
        },
        packModule(context, payload) {
            payload.moduleId = context.state.vectorId.toString();
            return context.state.client.post('/api/m/package', payload);
        },
        retrieveWorkspaceInformation(context, payload) {
            return context.state.client.get(`/api/workspace/update/${payload}`);
        },
        updateWorkspaceInformation(context, payload) {
            payload.update = true;
            return context.state.client.post('/api/workspace', payload);
        },
        cloneWorkspace(context, payload) {
            return context.state.client.post('/api/workspace/clone', payload);
        },
        deleteWorkspace(context, payload) {
            return context.state.client.post('/api/workspace/delete', payload);
        },
        loadModuleHistory(context, payload) {
            return context.state.client.get(`/api/workspace/history/${payload.moduleId}`);
        },
        revertChange(context, payload) {
            return context.state.client.post(`/api/m/revert`, payload);
        },
        async testConfiguration(context, payload) {
            return await context.state.client.post('/api/studio/test', {
                'script': payload.item.script,
                'payload': payload.item.payload,
                'vectorId': context.state.vectorId,
                'actual': payload.item.actual,
                'logicService': true
            });
        },
        async saveVector(context, payload) {
            let vectorToSubmit = _.cloneDeep(context.state.vector);

            const lists = ['actions', 'documents', 'flows', 'logicServices',
                'menus', 'notifications', 'states', 'triggers',
                'authorizations'];

            const informationList = ['connectionField', 'connectionId',
                'description', 'extra', 'displayKey', 'uniqueKey'];

            const toRemoveList = ['connectionModel', 'connectionFieldModel']

            if (vectorToSubmit.extension['information']) {
                vectorToSubmit.extension['information'].forEach(row => {
                    informationList.forEach(field => {
                        if (row[field] == null || row[field] == false) {
                            delete row[field];
                        }
                    });

                    toRemoveList.forEach(field => {
                        delete row[field];
                    });

                    if (row.fields) {
                        row.fields.forEach(tableField => {
                            informationList.forEach(columnField => {
                                if (tableField[columnField] == null || tableField[columnField] == false) {
                                    delete tableField[columnField];
                                }
                            });

                            toRemoveList.forEach(field => {
                                delete tableField[field];
                            });
                        });
                    }

                    delete row['columns'];
                });
            }

            lists.forEach(fieldList => {
                if (vectorToSubmit.extension[fieldList] == null
                    || vectorToSubmit.extension[fieldList].length == 0) {
                    delete vectorToSubmit.extension[fieldList];
                }
            });

            vectorToSubmit.dynamic.expansion = JSON.stringify(vectorToSubmit.extension);
            //delete vectorToSubmit.extension;

            vectorToSubmit.entityHandler = {
                manyToManyCollectionsToSave: ['Workspaces']
            };

            let payloadToSubmit = {
                vector: vectorToSubmit,
                activityId: payload && payload.activityId,
                description: payload && payload.description,
                newVersion: payload && payload.newVersion,
            }

            if (context.state.autoPush === false) {
                payloadToSubmit.autoPush = false;
            }

            let result = await context.state.client.post('/api/studio', payloadToSubmit);

            context.state.vector.audit.updatedDate = result.data.updatedDate;
            context.state.vector.id = result.data.id;
            context.commit('commitVector', context.state.vector);
            context.commit('setWorkspaceModule', context.state.vector);
            // context.commit('updateOrignal', vectorToSubmit);
            return result;
        },
        async loadModuleDesign({ state }, payload) {
            return await state.client.get(`/api/studio/${payload}`);
        },
        async loadAIVector(context, response) {
            let vm = this;
            let result = response;
            
            /* set module */
            let standardActivities = result.data.extension.standardActivities;
            Vue.set(result.data.extension, 'standardActivities', []);
            commons.standards.forEach(standard => {
                let found = standardActivities.find(x => x.id == standard.id);
                if (found) {
                    standardActivities = standardActivities.filter(item => item !== found);
                    result.data.extension.standardActivities.push(found);
                } else {
                    result.data.extension.standardActivities.push(standard);
                }
            });

            standardActivities.forEach(x => {
                if (result.data.extension.triggers
                    && result.data.extension.triggers.find(y => y.item == x.id)) {
                    result.data.extension.standardActivities.push(x);
                }
            });

            if (result.data.extension.connector) {
                result.data.extension.connectorText = JSON.stringify(result.data.extension.connector);
                delete result.data.extension.connector;
            }

            if (result.data.extension.mapping) {
                result.data.extension.mappingText = JSON.stringify(result.data.extension.mapping);
                delete result.data.extension.mapping;
            }

            context.commit('commitVector', result.data);
            commons.setChildMenu(context.state.workspace.menus, context.state.vector);
        },
        async loadVector(context, payload) {
            let vm = this;
            let result = await context.state.client.get(`/api/studio/${payload}`);
            /* load workspace */
            if (result.data.ownerWorkspaceId && (!context.state.workspace || context.state.workspace.id != result.data.ownerWorkspaceId)) {
                let workspaceResult = await context.state.client.get(`/api/workspace/${result.data.ownerWorkspaceId}?studio=studio`);
                authorizationStore.dispatch('setRoles', workspaceResult.data.roles);
                commons.expandMenu(workspaceResult.data.menus, workspaceResult.data.vectors, true);
                context.commit('commitWorkspace', workspaceResult.data);
            }
            /* set module */
            if (result.data.extension.standardActivities == null) {
                result.data.extension.standardActivities = commons.standards;
            } else {
                let standardActivities = result.data.extension.standardActivities;
                Vue.set(result.data.extension, 'standardActivities', []);
                commons.standards.forEach(standard => {
                    let found = standardActivities.find(x => x.id == standard.id);
                    if (found) {
                        standardActivities = standardActivities.filter(item => item !== found);
                        result.data.extension.standardActivities.push(found);
                    } else {
                        result.data.extension.standardActivities.push(standard);
                    }
                });

                standardActivities.forEach(x => {
                    if (result.data.extension.triggers
                        && result.data.extension.triggers.find(y => y.item == x.id)) {
                        result.data.extension.standardActivities.push(x);
                    }
                });
            }

            if (result.data.extension.connector) {
                result.data.extension.connectorText = JSON.stringify(result.data.extension.connector);
                delete result.data.extension.connector;
            }

            if (result.data.extension.mapping) {
                result.data.extension.mappingText = JSON.stringify(result.data.extension.mapping);
                delete result.data.extension.mapping;
            }

            context.commit('commitVector', result.data);
            commons.setChildMenu(context.state.workspace.menus, context.state.vector);
        },
        async installPackage({ state }, payload) {
            return await state.client.post(`/api/p/install`, payload);
        },
        async findVariable(context, payload) {
            if (payload.moduleId.toString() == context.state.vector.id.toString()) {
                let vector = _.cloneDeep(context.state.vector);
                vector.extension.standardActivities.forEach(standard => {
                    if (standard.design) {
                        commons.expandWorkDesign(vector.extension.information, standard.design);
                        standard.classification = commons.getAllElements(null, standard.design);
                    }
                });

                vector.extension.activities.forEach(activity => {
                    if (activity.design) {
                        commons.expandWorkDesign(vector.extension.information, activity.design);
                        activity.classification = commons.getAllElements(null, activity.design);
                    }
                });

                return {
                    id: vector.id,
                    emoji: vector.emoji,
                    name: vector.name,
                    standards: vector.extension.standardActivities,
                    activities: vector.extension.activities,
                    information: vector.extension.information,
                    isUserCollection: vector.isUserCollection
                }
            }

            if (!context.state.modules[payload.moduleId]) {
                if(!context.state.modulePromises[payload.moduleId]) {
                    let promise = context.state.client.get(`/api/m/${payload.moduleId}`);
                    context.commit('setModulePromise', {
                        moduleId: payload.moduleId,
                        promise: promise
                    })
                    let result = await promise;
                    context.commit('registerModule', result.data);
                }

                let result = await context.state.modulePromises[payload.moduleId];
            }

            return context.state.modules[payload.moduleId];
        },
        setUsers(context, payload) {
            let users = payload.map((x) => (
                Object.assign(x, {
                    username: x.username || x.userName
                })
            ))

            context.commit('commitUsers', users)
        },
        loadList(context, payload) {
            if (context.state.workspace && context.state.workspace.menus && context.state.workspace.menus.length) {
                let result = _.findDeep(context.state.workspace.menus, x => (x.id === payload), {
                    childrenPath: 'children'
                })
                if (result && result.value && result.parent && result.parent.id && result.parent.type != 'group') {
                    context.commit('commitList', {
                        id: result.value.id,
                        name: result.value.name,
                        emoji: result.value.emoji,
                        parentId: result.parent.id,
                    })
                    return
                }
            }

            context.commit('commitList', null)
        },
        async addVector({ state, dispatch, commit }, payload) {
            try {
                state.vector = {
                    name: payload.name,
                    emoji: payload.emoji,
                    description: payload.description,
                    ownerWorkspaceId: state.workspace.id,
                    extension: {
                        standardActivities: commons.standards
                    },
                    dynamic: {},
                    audit: {}
                }
                const res = await dispatch('saveVector');
                await dispatch('loadVector', res.data.id);
                dispatch('updateWorkspaceMenus');
            } catch (err) {
                throw err;
            }
        },
        async deleteVector(context, payload) {
            try {
                /* reconstruct menus */
                let workspace = _.cloneDeep(context.state.workspace)
                function deleteMenu(menus, id) {
                    let menu = menus.find(x => x.id == id);
                    if (menu) {
                        menus.splice(menus.indexOf(menu), 1);
                    } else {
                        let menuDeep = _.findDeep(menus, x => (x.id == id), { childrenPath: 'children' })
                        if (menuDeep && menuDeep.value && menuDeep.parent && Array.isArray(menuDeep.parent.children)) {
                            menuDeep.parent.children.splice(menuDeep.parent.children.indexOf(menuDeep.value), 1);

                            if (menuDeep.parent.type == 'group' && menuDeep.parent.children.length === 0)
                                deleteMenu(menus, menuDeep.parent.id);
                        }
                    }
                }
                deleteMenu(workspace.menus, payload);
                /* remove vector */
                workspace.vectors = workspace.vectors.filter(x => x.id != payload);
                /* commit workspace */
                context.commit('commitWorkspace', workspace);
                /* save workspace */
                await context.dispatch('saveWorkspace');
                /* delete vector */
                await context.state.client.post('/api/studio/delete/', {
                    id: payload,
                });
                /* load workspace */
                await context.dispatch('loadWorkspace', {
                    id: context.getters.workspaceId,
                });
                /* reset vector */
                context.dispatch('resetVector');
            } catch (err) {
                throw err;
            }
        },
        initialVector({ state, commit }) {
            let vector = {
                name: null,
                emoji: null,
                description: null,
                ownerWorkspaceId: state.workspace.id,
                extension: {
                    standardActivities: commons.standards
                },
                dynamic: {},
                audit: {}
            }
            commit("commitVector", vector);
        },
        resetVector(context, payload) {
            context.commit('commitVector', null)
        },
        async getFirstOrDefaultWorkspaceId({ dispatch }) {
            try {
                const res = await dispatch('searchWorkspace', { search: '' });
                if (Array.isArray(res) && res[0] && res[0].id) {
                    return res[0].id;
                } else {
                    throw 'error';
                }
            } catch(err) {
                console.error(err);
                throw {
                    status: 404,
                    error: 'No workspace found. Please contact your administrator.'
                };
            }
        },
        async loadWorkspace({ state, commit, dispatch }, payload) {
            try {
                let wsId = (payload && payload.id) || $cookies.get('wsId');
                if (!wsId) {
                    wsId = await dispatch('getFirstOrDefaultWorkspaceId');
                }
                let res = await state.client.get(`/api/workspace/${wsId}?studio=studio`);
                authorizationStore.dispatch('setRoles', res.data.roles);
                commit('commitWorkspace', res.data);
                commons.expandMenu(res.data.menus, res.data.vectors);
                return res.data;
            } catch(err) {
                throw err;
            }
        },
        async selectWorkspace(context, payload) {
            let workspaceResult = await context.state.client.get(`/api/workspace/${payload.id}?studio=studio`);
            authorizationStore.dispatch('setRoles', workspaceResult.data.roles);
            commons.expandMenu(workspaceResult.data.menus, workspaceResult.data.vectors);
            context.commit('commitWorkspace', workspaceResult.data);
            if (context.state.vector)
                commons.setChildMenu(context.state.workspace.menus, context.state.vector);
        },
        async searchWorkspace(context, payload) {
            const search = payload && payload.search ? encodeURIComponent(payload.search) : "";
            let result = await context.state.client.get(`/api/workspace?search=${search}&studio=studio`);
            return result.data;
        },
        async searchRecipient(context, payload) {
            let result = await context.state.client.get(`/api/studio/recipient?search=${payload.search}`);
            return result;
        },
        async filterElement(context, payload) {
            context.commit('filterElement', payload);
            let keyword = payload;
            if (keyword) {
                let lowerKeyword = keyword.toLowerCase();
                //if nothing, call AI
                let hasEntry = context.state.canvas.information.some(x => x.displayName.toLowerCase() == lowerKeyword)
                    || context.state.canvas.activities.some(x => x.name.toLowerCase() == lowerKeyword)
                    || context.state.canvas.standardActivities.some(x => x.name.toLowerCase() == lowerKeyword)
                    || context.state.canvas.documents.some(x => x.name.toLowerCase() == lowerKeyword)
                    || context.state.canvas.states.some(x => x.name.toLowerCase() == lowerKeyword);

                if (!hasEntry) {
                    let result = await context.state.client.get(`/api/ai?type=suggest&text=${encodeURIComponent(lowerKeyword)}`);
                    console.log(result.data);
                    let suggestions = [
                        ...result.data.user,
                        ...(result.data.suggest != null ? result.data.suggest : [])
                    ];

                    suggestions = suggestions.map(item => {
                        if (item['activity'] != null) {
                            return {
                                section: 'activity',
                                name: item.activity.name
                            }
                        } else if (item['state'] != null) {
                            return {
                                section: 'state',
                                name: item.state.name,
                                color: item.state.color
                            }
                        } else {
                            return {
                                section: 'information',
                                displayName: item.information.displayName,
                                type: item.information.type
                            }
                        }
                    });

                    suggestions = suggestions.slice(0, 5);

                    context.commit('suggestions', suggestions);

                } else {
                    context.commit('suggestions', []);
                }
            } else {
                context.commit('suggestions', []);
            }
        },
        async retrieveAuthorizationEntities(context, payload) {
            let result = await context.state.client.post(`/api/studio/authorizationselection`, payload);
            return result;
        },
        async saveWorkspace(context, payload) {
            if (context.state.workspace) {
                let menus = _.cloneDeep(context.state.workspace.menus);
                menus = commons.trimMenu(menus);
                return await context.state.client.post(`/api/workspace`, {
                    id: context.state.workspace.id,
                    menus,
                    lastUpdatedOn: context.state.workspace.lastUpdatedOn
                });
            }
        },
        async createWorkspace(context, payload) {
            let result = await context.state.client.post(`/api/workspace`, payload);
            await authorizationStore.dispatch('refreshToken');
            return result;
        },
        setWorkspaceName({ commit }, payload) {
            commit('setWorkspaceName', payload);
        },
        setWorkspaceLastUpdatedOn({ commit }, payload) {
            commit('commitWorkspaceLastUpdatedOn', payload);
        },
        async runScheduledEvent(context, payload) {
            let url = `api/ScheduledEventTrigger/run`;
            return await context.state.client.post(url, { id: payload._id });
        },
        async ask(context, payload) {
            let url = `api/studio/ask`;
            return await context.state.client.post(url, payload);
        },
        async loadNotifcationDashboard(context, payload) {
            let url = `/api/studio/notificationdashboard/${(payload && payload.moduleId) || context.state.vector.id}`;

            if(payload) {
                url += `?`;
                if(payload.searchTemplate) {
                    url += `searchTemplate=${payload.searchTemplate}`;
                }

                if(payload.searchDelivery) {
                    if(payload.searchTemplate)
                        url += '&';

                    url += `searchDelivery=${payload.searchDelivery}`;
                }
            }

            let result = await context.state.client.get(url);
            return result;
        },
        async ai(context, payload) {
            let result = await context.state.client.get(`/api/ai?type=${payload.type}&text=${payload.text}`);
            return result;
        },
        async retrieveSerialNumber(context, payload) {
            let result = await context.state.client.get(`/api/studio/serialnumber/${context.state.vector.id}`);
            return result;
        },
        async saveSerialNumber(context, payload) {
            try {
                payload.moduleId = context.state.vector.id;
                let result = await context.state.client.post(`/api/studio/serialnumber`, payload);
                return result;
            } catch (err) {
                throw err;
            }
        },
        async loadInformation(context, payload) {
            let result = await context.state.client.get(`/api/studio/information?id=${payload.name}`);
            return result;
        },
        async loadClassification(context, payload) {
            let result = await context.state.client.post(`/api/studio/classification`, payload);
            return result;
        },
        updateAuthorization(context, payload) {
            context.commit('commitUpdateAuthorization', payload);
        },
        setVectorTrigger({ commit }, data) {
            commit('setVectorTrigger', data)
        },
        removeVectorTrigger({ commit }, id) {
            commit('removeVectorTrigger', id)
        },
        updateWorkspaceMenus({ state, commit }) {
            let workspace = _.cloneDeep(state.workspace);
            workspace.menus.push({
                id: state.vector.id,
                emoji: state.vector.emoji,
                name: state.vector.name,
                children: [],
                module: state.vector.id,
                ownerWorkspaceId: workspace.id,
                currentWorkspace: true,
                published: true,
            });
            workspace.vectors.push({
                id: state.vector.id,
                emoji: state.vector.emoji,
                name: state.vector.name,
                menus: [],
            });
            commit('commitWorkspace', workspace);
        },
        updateVectorCanvas({ commit }, data) {
            commit("commitVectorCanvas", data);
        },
        setAutoPush({ commit }, data) {
            commit("commitAutoPush", data);
        },
        async loadModule({ state }, payload) {
            return await state.client.get(`/api/m/${payload}`);
        },
        async loadModuleInformation({ state }, payload) {
            try {
                const res = await state.client.get(`/api/m/${payload}`);
                return res.data.information;
            } catch (err) {
                throw err;
            }
        },
        /* connector:start */
        async testConnectSelection({ state }, payload) {
            try {
                return await state.client.post('/api/studio/testconnectselection', payload);
            } catch (err) {
                throw err;
            }
        },
        async testConnectorConnection({ state }, payload) {
            try {
                return await state.client.post('/api/studio/testconnect', payload);
            } catch (err) {
                throw err;
            }
        },
        async setConnector({ state }, payload) {
            try {
                return await state.client.post('/api/studio/connect', payload);
            } catch (err) {
                throw err;
            }
        }
    }
});